import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Component, useEffect, useState, lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { api, clearAuth, setAuth } from "@/lib/api";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CookieBanner from "@/components/layout/CookieBanner";
import StructuredData from "@/components/layout/StructuredData";
import FloatingActions from "@/components/layout/FloatingActions";

import Home from "@/pages/Home";

const RepairStart = lazy(() => import("@/pages/RepairStart"));
const RepairModels = lazy(() => import("@/pages/RepairModels"));
const RepairSelect = lazy(() => import("@/pages/RepairSelect"));
const RepairQuality = lazy(() => import("@/pages/RepairQuality"));
const RepairSummary = lazy(() => import("@/pages/RepairSummary"));
const Prices = lazy(() => import("@/pages/Prices"));
const Appointment = lazy(() => import("@/pages/Appointment"));
const RepairRequest = lazy(() => import("@/pages/RepairRequest"));
const Shipping = lazy(() => import("@/pages/Shipping"));
const Status = lazy(() => import("@/pages/Status"));
const Locations = lazy(() => import("@/pages/Locations"));
const Contact = lazy(() => import("@/pages/Contact"));
const About = lazy(() => import("@/pages/About"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Warranty = lazy(() => import("@/pages/Warranty"));
const DataSecurity = lazy(() => import("@/pages/DataSecurity"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const Confirmation = lazy(() => import("@/pages/Confirmation"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AuthCallback = lazy(() => import("@/pages/admin/AuthCallback"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminOrderDetail = lazy(() => import("@/pages/admin/AdminOrderDetail"));
const AdminDeviceIntakes = lazy(() => import("@/pages/admin/AdminDeviceIntakes"));
const AdminCatalog = lazy(() => import("@/pages/admin/AdminCatalog"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminMessages = lazy(() => import("@/pages/admin/AdminMessages"));
const AdminInvoices = lazy(() => import("@/pages/admin/AdminInvoices"));
const AdminWarranty = lazy(() => import("@/pages/admin/AdminWarranty"));
const AdminStats = lazy(() => import("@/pages/admin/AdminStats"));
const AdminInventory = lazy(() => import("@/pages/admin/AdminInventory"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminPhoneCatalog = lazy(() => import("@/pages/admin/AdminPhoneCatalog"));
const AdminQualityOptions = lazy(() => import("@/pages/admin/AdminQualityOptions"));
const AdminCompany = lazy(() => import("@/pages/admin/AdminCompany"));
const AdminLocations = lazy(() => import("@/pages/admin/AdminLocations"));
const AdminAppointmentSettings = lazy(() => import("@/pages/admin/AdminAppointmentSettings"));
const AdminEmailTemplates = lazy(() => import("@/pages/admin/AdminEmailTemplates"));
const AdminFAQ = lazy(() => import("@/pages/admin/AdminFAQ"));
const AdminSiteStructure = lazy(() => import("@/pages/admin/AdminSiteStructure"));

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) { console.error("App render error", error); }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="min-h-screen grid place-items-center bg-[#08080a] px-6" data-testid="app-error-fallback">
        <div className="premium-card max-w-lg p-8 text-center">
          <div className="section-label text-[#4d9aff]">Handy Meister</div>
          <h1 className="mt-3 font-display text-3xl font-bold">Diese Seite konnte nicht geladen werden.</h1>
          <p className="mt-3 text-zinc-400">Bitte laden Sie die Seite erneut. Ihre Auswahl bleibt gespeichert.</p>
          <button onClick={() => window.location.reload()} className="mt-6 btn-primary" data-testid="app-error-reload">Neu laden</button>
        </div>
      </main>
    );
  }
}


function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <StructuredData />
      <Header />
      <main className="pb-24 xl:pb-0">{children}</main>
      <Footer />
      <FloatingActions />
      <CookieBanner />
    </>
  );
}

function P({ children }) { return <PublicLayout>{children}</PublicLayout>; }

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppErrorBoundary>
        <ScrollToTop />
        <Toaster richColors position="top-center" />
        <Suspense fallback={<div className="min-h-[50vh] grid place-items-center" data-testid="route-loading"><Loader2 className="animate-spin text-[#4d9aff]"/></div>}>
          <AuthRouter />
        </Suspense>
        </AppErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

/**
 * Intercept the `/admin#session_id=…` redirect from Emergent OAuth before the
 * normal router runs — otherwise an admin route may redirect before the
 * one-time session exchange completes.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AdminRoute({ children }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [state, setState] = useState("checking");

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => {
        setAuth(null, data);
        setState("authenticated");
      })
      .catch(() => {
        clearAuth();
        setState("unauthenticated");
        nav("/admin/login", { replace: true, state: { from: loc.pathname } });
      });
  }, [loc.pathname, nav]);

  if (state !== "authenticated") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0a0a0b]" data-testid="admin-auth-checking">
        <Loader2 size={24} className="animate-spin text-[#0055FF]" />
      </div>
    );
  }
  return children;
}

function AuthRouter() {
  const loc = useLocation();
  if ((loc.hash || "").includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
          {/* Public */}
          <Route path="/" element={<P><Home /></P>} />
          <Route path="/reparatur" element={<P><RepairStart /></P>} />
          <Route path="/reparatur/modelle" element={<P><RepairModels /></P>} />
          <Route path="/reparatur/auswahl" element={<P><RepairSelect /></P>} />
          <Route path="/reparatur/qualitaet" element={<P><RepairQuality /></P>} />
          <Route path="/reparatur/uebersicht" element={<P><RepairSummary /></P>} />
          <Route path="/preise" element={<P><Prices /></P>} />
          <Route path="/termin-buchen" element={<P><Appointment /></P>} />
          <Route path="/reparaturanfrage" element={<P><RepairRequest /></P>} />
          <Route path="/versand-reparatur" element={<P><Shipping /></P>} />
          <Route path="/reparaturstatus" element={<P><Status /></P>} />
          <Route path="/bestaetigung/:number" element={<P><Confirmation /></P>} />
          <Route path="/standorte" element={<P><Locations /></P>} />
          <Route path="/kontakt" element={<P><Contact /></P>} />
          <Route path="/ueber-uns" element={<P><About /></P>} />
          <Route path="/faq" element={<P><FAQ /></P>} />
          <Route path="/garantie" element={<P><Warranty /></P>} />
          <Route path="/datenschutz" element={<P><DataSecurity /></P>} />
          <Route path="/impressum" element={<P><LegalPage type="impressum" /></P>} />
          <Route path="/datenschutzerklaerung" element={<P><LegalPage type="datenschutz" /></P>} />
          <Route path="/agb" element={<P><LegalPage type="agb" /></P>} />
          <Route path="/reparaturbedingungen" element={<P><LegalPage type="reparatur" /></P>} />
          <Route path="/widerruf" element={<P><LegalPage type="widerruf" /></P>} />
          <Route path="/cookie-einstellungen" element={<P><LegalPage type="cookies" /></P>} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
          <Route path="/admin/orders/:id" element={<AdminRoute><AdminOrderDetail /></AdminRoute>} />
          <Route path="/admin/device-intakes" element={<AdminRoute><AdminDeviceIntakes /></AdminRoute>} />
          <Route path="/admin/catalog" element={<AdminRoute><AdminCatalog /></AdminRoute>} />
          <Route path="/admin/phone-catalog" element={<AdminRoute><AdminPhoneCatalog /></AdminRoute>} />
          <Route path="/admin/quality-options" element={<AdminRoute><AdminQualityOptions /></AdminRoute>} />
          <Route path="/admin/company" element={<AdminRoute><AdminCompany /></AdminRoute>} />
          <Route path="/admin/locations" element={<AdminRoute><AdminLocations /></AdminRoute>} />
          <Route path="/admin/appointment-settings" element={<AdminRoute><AdminAppointmentSettings /></AdminRoute>} />
          <Route path="/admin/email-templates" element={<AdminRoute><AdminEmailTemplates /></AdminRoute>} />
          <Route path="/admin/faqs" element={<AdminRoute><AdminFAQ /></AdminRoute>} />
          <Route path="/admin/site-structure" element={<AdminRoute><AdminSiteStructure /></AdminRoute>} />
          <Route path="/admin/content" element={<AdminRoute><AdminContent /></AdminRoute>} />
          <Route path="/admin/invoices" element={<AdminRoute><AdminInvoices /></AdminRoute>} />
          <Route path="/admin/warranty" element={<AdminRoute><AdminWarranty /></AdminRoute>} />
          <Route path="/admin/stats" element={<AdminRoute><AdminStats /></AdminRoute>} />
          <Route path="/admin/inventory" element={<AdminRoute><AdminInventory /></AdminRoute>} />
          <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
          <Route path="/admin/messages" element={<AdminRoute><AdminMessages /></AdminRoute>} />

          <Route path="*" element={<P><div className="mx-auto max-w-3xl px-6 py-32 text-center"><h1 className="font-display text-4xl">404 — Seite nicht gefunden</h1></div></P>} />
        </Routes>
  );
}

export default App;
